import { Stack, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useRef } from "react";

import {
  CommonGenerationTileProps,
  GenerationTileAction,
} from "@/components/Tiles/GenerationTile/types";
import {
  ControlledAudio,
  TileBase,
  TileLikeButton,
  TileObjectTypeIndicator,
  Tooltip,
  Typography,
} from "@/components/ui-v2";
import { AudioIcon } from "@/components/ui-v2/icons";
import { SessionFileFragment } from "@/features/forge/forge-session/queries.graphql";
import { useOnValueChanged } from "@/hooks";
import { getDisplayPrompt } from "@/utils/getDisplayPrompt";

import { AudioGenerationTileButtons } from "./AudioGenerationTileButtons";
import { AudioGenerationTileControls } from "./AudioGenerationTileControls";

export interface AudioGenerationTileProps
  extends CommonGenerationTileProps<SessionFileFragment> {
  url?: string;
  isPlaying?: boolean;
}

const StyledAudioIcon = styled(AudioIcon)({
  fontSize: "4em",
});

export const AudioGenerationTile = (props: AudioGenerationTileProps) => {
  const {
    object,
    isSelected = false,
    isSelectable = false,
    isLiking = false,
    isPlaying = false,
    onAction,
    ...rest
  } = props;
  const theme = useTheme();
  const audioRef = useRef<HTMLAudioElement>(null);

  const { url, isLiked, inferenceParameters } = object;
  const prompt = getDisplayPrompt(inferenceParameters);

  const handleIsPlayingChanged = () => {
    const audioEl = audioRef.current;

    // Reset audio when tile is unselected and audio was playing
    if (!isPlaying && !isSelected && audioEl) {
      audioEl.currentTime = 0;
    }
  };

  const handleTileBaseAction =
    (action: GenerationTileAction) => (e: React.MouseEvent) => {
      onAction?.(e, action);
    };

  const handleAudioEnded = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    onAction?.(e, "TOGGLE_PLAY");
  };

  useOnValueChanged(isPlaying, handleIsPlayingChanged);

  return (
    <TileBase
      isSelected={isSelected}
      isSelectable={isSelectable}
      sx={{ background: theme.palette.secondary[6] }}
      slots={{
        bottomLeftEl: AudioGenerationTileButtons,
        topLeftEl: TileObjectTypeIndicator,
        topRightEl: url ? AudioGenerationTileControls : undefined,
        bottomRightEl: TileLikeButton,
      }}
      slotProps={{
        bottomLeftEl: {
          onAction,
        },
        topLeftEl: {
          objectType: "audio",
        },
        topRightEl: {
          isPlaying,
          onAction,
        },
        bottomRightEl: {
          isLoading: isLiking,
          isLiked,
          onAction,
          sx: {
            m: 1,
          },
        },
      }}
      onClick={handleTileBaseAction("SELECT")}
      onDoubleClick={handleTileBaseAction("DOUBLE_CLICK")}
      onContextMenu={handleTileBaseAction("OPEN_CONTEXT_MENU")}
      {...rest}
    >
      <Stack
        sx={{
          width: "100%",
          alignItems: "center",
          gap: "1.5em",
        }}
      >
        <ControlledAudio
          src={url}
          isPlaying={isPlaying}
          onEnded={handleAudioEnded}
          ref={audioRef}
        />
        <StyledAudioIcon />
        {prompt && (
          <Tooltip
            title={prompt}
            placement="top"
            sx={{
              [theme.breakpoints.down("md")]: {
                display: "none",
              },
            }}
          >
            <Typography
              noWrap
              color="text.secondary"
              sx={{
                maxWidth: "80%",
                backgroundColor: theme.palette.secondary[5],
                borderRadius: 3,
                p: theme.spacing(1, 2),
                fontSize: "0.8em",
                lineHeight: "normal",
              }}
            >
              {prompt}
            </Typography>
          </Tooltip>
        )}
      </Stack>
    </TileBase>
  );
};
